/**
 * SeeMe Tag Input
 * Multi-select tag widget: inline chips + "+" trigger that opens a
 * dropdown with search, filter, and create-new.
 *
 * Two things in the markup below are conventions, not requirements:
 *   - chip-sm is illustrative. Any chip size works (chip-xs is common in dense
 *     CRM cards) as long as the badges and the trigger agree — added chips copy
 *     the size from whichever is present. Don't re-hardcode it.
 *   - The .tag-input__search block is optional; omit it when there are only a
 *     few options. The empty state works without it.
 *
 * Markup:
 *   <div class="tag-input" data-tag-input>
 *     <div class="tag-input__tags">
 *       <span class="chip chip-sm" data-tag-input-badge="1">
 *         <span class="chip-label">Tag Name</span>
 *         <button class="btn-dismiss" data-tag-input-remove="1">
 *           <i class="fa-solid fa-xmark"></i>
 *         </button>
 *       </span>
 *       <button type="button" class="chip chip-sm" data-tag-input-trigger>
 *         <i class="chip-icon fa-solid fa-plus"></i>
 *       </button>
 *     </div>
 *     <div class="tag-input__menu">
 *       <div class="tag-input__search">
 *         <input class="form-control form-control-sm" placeholder="Search..."
 *                data-tag-input-search>
 *       </div>
 *       <div class="tag-input__list" data-tag-input-list>
 *         <a class="dropdown-item" data-tag-input-option="1" href="#"
 *            data-tag-selected style="display:none">Tag 1</a>
 *         <a class="dropdown-item" data-tag-input-option="2" href="#">Tag 2</a>
 *       </div>
 *       <div class="tag-input__empty">No matching tags</div>
 *       <hr class="dropdown-divider tag-input__create-divider">
 *       <a class="dropdown-item tag-input__create" href="#"
 *          data-tag-input-create style="display:none">
 *         <i class="fa-solid fa-plus"></i> Create new
 *       </a>
 *     </div>
 *   </div>
 *
 * Events (dispatched on the [data-tag-input] element):
 *   taginput:add    — detail: { id, name }  (existing option selected)
 *   taginput:remove  — detail: { id, name }  (chip removed)
 *   taginput:create  — detail: { name }      (new tag requested)
 */

(function () {
    'use strict';

    // -- Helpers --

    function getContainer(el) {
        return el.closest('[data-tag-input]');
    }

    // Mirrors the trigger-visibility rule in tag-input.less: is there anything
    // left for the "+" to offer? An unselected option, or a create affordance
    // (the user can still type a new tag even with every option taken).
    // Keep the two in step — the CSS decides whether the trigger shows, this
    // decides whether the menu stays open with it.
    function hasAddableOption(container) {
        return !!container.querySelector('[data-tag-input-option]:not([data-tag-selected])') ||
               !!container.querySelector('[data-tag-input-create]');
    }

    function getVisibleOptions(container) {
        var list = container.querySelector('[data-tag-input-list]');
        if (!list) return [];
        return Array.prototype.filter.call(
            list.querySelectorAll('[data-tag-input-option]'),
            function (el) { return el.style.display !== 'none' && !el.hasAttribute('data-tag-selected'); }
        );
    }

    function clearFocus(container) {
        container.querySelectorAll('.dropdown-item.focused').forEach(function (el) {
            el.classList.remove('focused');
        });
    }

    function escapeHtml(str) {
        var div = document.createElement('div');
        div.appendChild(document.createTextNode(str));
        return div.innerHTML;
    }

    // -- Menu open/close --

    function openMenu(container) {
        var menu = container.querySelector('.tag-input__menu');
        if (!menu) return;

        // Reset positioning before measuring
        menu.style.left = '';
        menu.style.right = '';
        menu.style.top = '';
        menu.style.bottom = '';
        menu.classList.remove('tag-input__menu--up');
        menu.classList.add('show');

        // Flip if overflowing viewport
        requestAnimationFrame(function () {
            var rect = menu.getBoundingClientRect();

            // Flip left if overflowing right edge
            if (rect.right > window.innerWidth) {
                menu.style.left = 'auto';
                menu.style.right = '0';
            }

            // Flip up if overflowing bottom edge
            if (rect.bottom > window.innerHeight) {
                menu.style.top = 'auto';
                menu.style.bottom = '100%';
                menu.style.marginTop = '';
                menu.style.marginBottom = '4px';
            }

            // filterOptions() is the only thing that toggles .tag-input__empty,
            // so it must run whether or not a search box exists. A tag input
            // built without [data-tag-input-search] is legitimate (two or three
            // options don't need filtering); gating this on the input left such
            // a widget showing a blank sliver instead of its empty state once
            // every option was selected.
            var input = container.querySelector('[data-tag-input-search]');
            if (input) input.value = '';
            filterOptions(container, '');
            if (input) input.focus();
        });
    }

    function closeMenu(container) {
        var menu = container.querySelector('.tag-input__menu');
        if (!menu) return;
        menu.classList.remove('show');
        menu.style.left = '';
        menu.style.right = '';
        menu.style.top = '';
        menu.style.bottom = '';
        menu.style.marginBottom = '';
        clearFocus(container);
    }

    function isMenuOpen(container) {
        var menu = container.querySelector('.tag-input__menu');
        return menu && menu.classList.contains('show');
    }

    // -- Filter --

    function filterOptions(container, query) {
        var list = container.querySelector('[data-tag-input-list]');
        var emptyMsg = container.querySelector('.tag-input__empty');
        if (!list) return;

        var items = list.querySelectorAll('[data-tag-input-option]');
        var q = query.toLowerCase().trim();
        var visibleCount = 0;
        var exactMatch = false;

        items.forEach(function (item) {
            if (item.hasAttribute('data-tag-selected')) {
                item.style.display = 'none';
                return;
            }
            var text = item.textContent.toLowerCase().trim();
            var match = !q || text.indexOf(q) !== -1;
            item.style.display = match ? '' : 'none';
            if (match) visibleCount++;
            if (text === q) exactMatch = true;
        });

        if (emptyMsg) {
            emptyMsg.classList.toggle('show', visibleCount === 0);
        }

        var createBtn = container.querySelector('[data-tag-input-create]');
        var createDivider = container.querySelector('.tag-input__create-divider');
        if (createBtn) {
            if (q && !exactMatch) {
                createBtn.style.display = '';
                if (createDivider) createDivider.style.display = '';
                createBtn.innerHTML = '<i class="fa-solid fa-plus"></i> Create "' + escapeHtml(query.trim()) + '"';
            } else {
                createBtn.style.display = 'none';
                if (createDivider) createDivider.style.display = 'none';
            }
        }

        clearFocus(container);
    }

    // -- Add tag (from existing option) --

    // Match the chip size the markup authored, rather than assuming chip-sm.
    // The row only stays aligned if a client-added chip matches its server-
    // rendered neighbours, and docs/design/components/forms.md § Chip sizing
    // requires the size to be on both the badges and the + trigger — so either
    // can model it. Size class only: chips often carry a per-tag colour variant
    // (and the trigger carries .chip-placeholder), none of which belongs on a
    // newly added tag.
    function chipSizeClass(container) {
        var model = container.querySelector('[data-tag-input-badge]') ||
                    container.querySelector('[data-tag-input-trigger]');
        var match = model && model.className.match(/\bchip-(xs|sm|lg)\b/);
        return match ? match[0] : 'chip-sm';
    }

    function addTag(container, option) {
        var id = option.getAttribute('data-tag-input-option');
        var name = option.textContent.trim();

        option.setAttribute('data-tag-selected', '');
        option.style.display = 'none';

        // Create chip
        var chip = document.createElement('span');
        chip.className = 'chip ' + chipSizeClass(container);
        chip.setAttribute('data-tag-input-badge', id);
        var label = document.createElement('span');
        label.className = 'chip-label';
        label.textContent = name;
        chip.appendChild(label);
        var removeBtn = document.createElement('button');
        removeBtn.className = 'btn-dismiss';
        removeBtn.setAttribute('data-tag-input-remove', id);
        removeBtn.innerHTML = '<i class="fa-solid fa-xmark"></i>';
        removeBtn.type = 'button';
        chip.appendChild(removeBtn);

        // Insert chip before the + trigger
        var trigger = container.querySelector('[data-tag-input-trigger]');
        if (trigger) {
            trigger.parentNode.insertBefore(chip, trigger);
        } else {
            container.querySelector('.tag-input__tags').appendChild(chip);
        }

        // Re-filter — unguarded for the same reason as openMenu: this is what
        // reveals the empty state once the last option has been selected.
        var input = container.querySelector('[data-tag-input-search]');
        if (input) input.value = '';
        filterOptions(container, '');

        // Taking the last option hides the trigger (tag-input.less). Close the
        // menu with it, or the selection leaves a "No matching tags" popover
        // hanging off a control that is no longer there.
        if (!hasAddableOption(container)) closeMenu(container);

        container.dispatchEvent(new CustomEvent('taginput:add', {
            bubbles: true,
            detail: { id: id, name: name }
        }));
    }

    // -- Remove tag --

    function removeTag(container, id) {
        var chip = container.querySelector('[data-tag-input-badge="' + id + '"]');
        var label = chip ? chip.querySelector('.chip-label') : null;
        var name = label ? label.textContent.trim() : '';
        if (chip) chip.remove();

        var option = container.querySelector('[data-tag-input-option="' + id + '"]');
        if (option) {
            option.removeAttribute('data-tag-selected');
            var input = container.querySelector('[data-tag-input-search]');
            filterOptions(container, input ? input.value : '');
        }

        container.dispatchEvent(new CustomEvent('taginput:remove', {
            bubbles: true,
            detail: { id: id, name: name }
        }));
    }

    // -- Event Handlers --

    // Click + trigger → toggle menu
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-tag-input-trigger]');
        if (!trigger) return;
        e.preventDefault();
        var container = getContainer(trigger);
        if (!container) return;
        if (isMenuOpen(container)) {
            closeMenu(container);
        } else {
            openMenu(container);
        }
    });

    // Type in search → filter
    document.addEventListener('input', function (e) {
        if (!e.target.hasAttribute('data-tag-input-search')) return;
        var container = getContainer(e.target);
        if (!container) return;
        filterOptions(container, e.target.value);
    });

    // Click option → add tag
    document.addEventListener('click', function (e) {
        var option = e.target.closest('[data-tag-input-option]');
        if (!option) return;
        var container = getContainer(option);
        if (!container) return;
        e.preventDefault();
        addTag(container, option);
    });

    // Click remove → remove tag
    document.addEventListener('click', function (e) {
        var removeBtn = e.target.closest('[data-tag-input-remove]');
        if (!removeBtn) return;
        var container = getContainer(removeBtn);
        if (!container) return;
        e.preventDefault();
        var id = removeBtn.getAttribute('data-tag-input-remove');
        removeTag(container, id);
    });

    // Click "Create new"
    document.addEventListener('click', function (e) {
        var createBtn = e.target.closest('[data-tag-input-create]');
        if (!createBtn) return;
        var container = getContainer(createBtn);
        if (!container) return;
        e.preventDefault();
        var input = container.querySelector('[data-tag-input-search]');
        var name = input ? input.value.trim() : '';
        if (!name) return;

        container.dispatchEvent(new CustomEvent('taginput:create', {
            bubbles: true,
            detail: { name: name }
        }));
    });

    // Escape closes an open menu and stops there. Capture phase because Houses
    // registers this file after sheets.js, whose Escape listener would run first.
    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var input = e.target.closest && e.target.closest('[data-tag-input-search]');
        var container = input && getContainer(input);
        if (!container || !isMenuOpen(container)) return;
        e.stopImmediatePropagation();
        closeMenu(container);
    }, true);

    // Keyboard: arrows, Enter
    document.addEventListener('keydown', function (e) {
        var input = e.target.closest('[data-tag-input-search]');
        if (!input) return;
        var container = getContainer(input);
        if (!container) return;

        var items = getVisibleOptions(container);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            var focused = container.querySelector('.dropdown-item.focused');
            var idx = focused ? items.indexOf(focused) : -1;
            clearFocus(container);
            idx = idx < items.length - 1 ? idx + 1 : 0;
            if (items[idx]) {
                items[idx].classList.add('focused');
                items[idx].scrollIntoView({ block: 'nearest' });
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            var focused = container.querySelector('.dropdown-item.focused');
            var idx = focused ? items.indexOf(focused) : -1;
            clearFocus(container);
            idx = idx > 0 ? idx - 1 : items.length - 1;
            if (items[idx]) {
                items[idx].classList.add('focused');
                items[idx].scrollIntoView({ block: 'nearest' });
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            var focused = container.querySelector('.dropdown-item.focused');
            if (focused && focused.hasAttribute('data-tag-input-option')) {
                addTag(container, focused);
            } else if (input.value.trim()) {
                var q = input.value.trim().toLowerCase();
                var exactOption = null;
                items.forEach(function (item) {
                    if (item.textContent.trim().toLowerCase() === q) exactOption = item;
                });
                if (exactOption) {
                    addTag(container, exactOption);
                } else {
                    container.dispatchEvent(new CustomEvent('taginput:create', {
                        bubbles: true,
                        detail: { name: input.value.trim() }
                    }));
                }
            }
        }
    });

    // Click outside → close menu
    document.addEventListener('click', function (e) {
        document.querySelectorAll('[data-tag-input]').forEach(function (container) {
            if (!container.contains(e.target)) {
                closeMenu(container);
            }
        });
    });

    // Expose API for programmatic use
    window.SeeMe = window.SeeMe || {};
    window.SeeMe.tagInput = {
        addOption: function (container, id, name) {
            var list = container.querySelector('[data-tag-input-list]');
            if (!list) return;

            var opt = document.createElement('a');
            opt.className = 'dropdown-item';
            opt.href = '#';
            opt.setAttribute('data-tag-input-option', id);
            opt.textContent = name;
            list.appendChild(opt);

            addTag(container, opt);
        },
        closeMenu: closeMenu
    };

})();
